import { _ } from "../../../core/polyglot/polyglot.js";
import { debounce } from "../../../core/helpers/functional-util.js";
import { magicIcon } from "../../../core/elements/icons/index.js";
import { makeMonitorRepository } from "../../../core/repositories/repositoryBuilders.js";
import { select } from "../../../core/elements/index.js";
import accountingDocument from "../../shared/accountingDocument.js";
import AlertMessageWidget from "../../../core/widgets/AlertMessageWidget/AlertMessageWidget.js";
import Budget from "../../../models/Budget.js";
import BudgetAutomationService from "./BudgetAutomationService.js";
import BudgetValuesPreviewWidget from "./BudgetValuesPreviewWidget.js";
import ChartOfAccounts from "../../../models/chartOfAccounts/ChartOfAccounts.js";
import keyFigureHighchartsOptions from "../../../models/keyFigure/rendering/keyFigureHighchartsOptions.js";
import MultiplePercentageVariationEditorWidget from "./MultiplePercentageVariationEditorWidget.js";
import session from "../../../core/session.js";

/**
 * The budget automation document renders an interface for filling budget data
 * automatically based on previous fiscal year's data.
 *
 * The user can select a year, then a variation in percentage of income & costs,
 * as budget values.
 */
const budgetAutomationDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		my.budgetAutomationService = new BudgetAutomationService();

		that.title = (html) => {
			html.render(magicIcon, _("Budget automation"));
		};

		that.subtitle = (html) => {
			html.render(
				_("Setup your budget using previous year's income and costs"),
			);
		};

		that.documentId = "budget-automation";

		my.budget = null;
		my.incomePercentageVariation = 0;
		my.costPercentageVariation = 0;

		const antiForgeryToken = session.getAntiForgeryToken();

		my.budgetRepository = makeMonitorRepository({
			route: "Accounting2/Company/#companyId/Budget/?budgetId",
			model: Budget,
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
			},
			antiForgeryToken,
		});

		my.chartOfAccountsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/ChartOfAccounts/#id",
			model: ChartOfAccounts,
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
				id: session.getCurrentChartOfAccountsId(),
			},
			antiForgeryToken,
		});

		that.showEmpty();
	};

	my.initializeSubwidgets = function () {
		my.growthVariationEditor = new MultiplePercentageVariationEditorWidget({
			items: [
				{
					label: _("Income"),
					name: "income",
					barColor: keyFigureHighchartsOptions.Income.color,
					onChange: (value) => {
						my.incomePercentageVariation = value;
						my.debouncedFillBudget();
					},
				},
				{
					label: _("Costs"),
					name: "costs",
					barColor: keyFigureHighchartsOptions.Cost.color,
					onChange: (value) => {
						my.costPercentageVariation = value;
						my.debouncedFillBudget();
					},
				},
			],
		});

		my.selectedFiscalYear = null;

		my.valuesPreview = new BudgetValuesPreviewWidget({ budget: my.budget });

		const invalidSelectionError = _(
			"You are trying to apply data of different period length to this budget. This scenario is unfortunatly not supported yet. If this is requested please contact support so that we know this is needed.",
		);

		my.warningMessage = new AlertMessageWidget({
			title: _("Please select a different fiscal year."),
			content: invalidSelectionError,
			dismissible: false,
			type: "warning",
			dataTestName: "incompatible-fiscal-year",
		});
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	that.hasActionsMenu = function () {
		return false;
	};

	that.show = async function (budgetId) {
		that.showUpdating();

		const [chartOfAccounts, budget] = await Promise.all([
			my.chartOfAccountsRepository.get(),
			my.budgetRepository.get({ filter: { budgetId } }),
		]);

		my.valuesPreview.setChartOfAccounts(chartOfAccounts);
		my.setBudget(budget);
		that.showContent();
	};

	my.renderContentOn = function (html) {
		html.render(
			my.section({
				title: _("Fiscal year data"),
				content: my.renderFiscalYearPickerOn,
			}),
			my.section({
				title: _("Variation from fiscal year"),
				content: my.renderVariationEditorOn,
			}),
			my.section({
				title: _("Budget values"),
				content: [my.valuesPreview, my.renderActionsOn],
			}),
		);
	};

	my.renderFiscalYearPickerOn = function (html) {
		html.div(
			{ klass: "help" },
			_("Select the fiscal year that the budget will be based on."),
		);

		if (!my.isSelectionValid()) html.render(my.warningMessage);

		html.render(
			select({
				items: session
					.getCompany()
					.getActiveYears()
					.reverse()
					.map((year) => ({
						label: year.getName(),
						value: year,
					})),
				selected: my.selectedFiscalYear,
				onChange: (value) => {
					my.selectedFiscalYear = value;
					that.update();
					my.fillBudget();
				},
			}),
		);
	};

	my.renderVariationEditorOn = function (html) {
		html.render(my.growthVariationEditor);
	};

	my.renderActionsOn = function (html) {
		html.div(
			{ klass: "actions" },
			html.a(
				{
					klass: "btn btn-default",
					click: my.redirectToBudgetEditor,
				},
				_("Cancel"),
			),
			html.a(
				{
					klass: [
						"btn",
						"btn-primary",
						{ disabled: !my.isSelectionValid() },
					],
					"data-test": "save",
					click: my.saveBudget,
				},
				_("Save"),
			),
		);
	};

	my.setBudget = function (budget) {
		my.budget = budget;
		my.valuesPreview.setBudget(budget);
	};

	my.saveBudget = async function () {
		await my.budgetRepository.update({
			model: my.budget,
			filter: { budgetId: my.budget.getId() },
			notify: false,
		});

		my.redirectToBudgetEditor();
	};

	my.redirectToBudgetEditor = function () {
		my.redirectTo(`/budget-editor/${my.budget.getId()}`);
	};

	my.fillBudget = async function () {
		if (!my.isSelectionValid()) {
			return;
		}

		const budget = await my.budgetAutomationService.fillBudget({
			budget: my.budget,
			fiscalYear: my.selectedFiscalYear,
			costGrowthPercentage: my.costPercentageVariation,
			incomeGrowthPercentage: my.incomePercentageVariation,
		});

		my.setBudget(budget);
	};

	my.debouncedFillBudget = debounce({ fn: my.fillBudget, delay: 1000 });

	my.isSelectionValid = function () {
		return my.selectedFiscalYear?.hasSameMonthsRange(
			my.budget.getFiscalYear(),
		);
	};
});

export default budgetAutomationDocument;
